import { useMutation, UseMutationOptions, useQuery } from 'react-query';
import { AxiosError, AxiosResponse } from 'axios';

import {
  AgencyFilterDto,
  AgencyResponseDto,
  CaseSupportFormRequestDto,
  CaseSupportFormResponseDto,
  PagedResponse,
} from '@bandido/shared/dtos';
import { QueryKeys } from '../../common/types';
import { client } from '../../common/services';
import { AgencyGetResponseDto } from '@bandido/shared/dtos';

export const NEW_SUPPORT_FORM_ID = 'new';

interface useGetMyAgencyProps  {
  onErrorCallback?: (_e: AxiosError) => void;
}

interface useGetAllAgencyProps extends AgencyFilterDto,useGetMyAgencyProps {
}



interface GetSupportFormsByAgency extends Omit<AgencyFilterDto, 'agencyId'>,useGetMyAgencyProps  {
  agencyId?: string | number;
}

interface GetByByIdProps extends useGetMyAgencyProps {
  id?: string | number;
  params?: Record<string, string>;
}

// QUERIES

export const useGetMyAgencies = ({ onErrorCallback }: useGetMyAgencyProps) =>
  useQuery({
    queryKey: [QueryKeys.getMyAgencies],
    queryFn: async (): Promise<AgencyGetResponseDto[] | undefined> => {
      try {
        const agencies: AxiosResponse<AgencyGetResponseDto[]> = await client.get('agency/mine');
        return agencies.data;
      } catch (e) {
        onErrorCallback && onErrorCallback(e as AxiosError);
      }
    },
    refetchOnWindowFocus: true,
  });

export const useGetAllAgencies = ({ currentPage, onErrorCallback, pageSize, search }: useGetAllAgencyProps) =>
  useQuery({
    queryKey: [QueryKeys.getAllAgencies, currentPage, pageSize, search],
    queryFn: async (): Promise<PagedResponse<AgencyResponseDto> | undefined> => {
      try {
        const users: AxiosResponse<PagedResponse<AgencyResponseDto>, any> = await client.get('agency', {
          params: {
            currentPage,
            pageSize,
            search,
          },
        });

        return users.data;
      } catch (e) {
        onErrorCallback && onErrorCallback(e as AxiosError);
      }
    },
    refetchOnWindowFocus: true,
  });

export const useGetAgencyById = ({ id, onErrorCallback }: GetByByIdProps) =>
  useQuery({
    queryKey: [QueryKeys.getAgencyById, id],
    queryFn: async (): Promise<AgencyResponseDto | undefined> => {
      try {
        const result: AxiosResponse<AgencyResponseDto, any> = await client.get(`agency/${id}`);

        return result.data;
      } catch (e) {
        onErrorCallback && onErrorCallback(e as AxiosError);
      }
    },
    refetchOnWindowFocus: true,
    enabled: Boolean(id),
  });

export const useGetSettingsByAgencyId = ({ id, params, onErrorCallback }: GetByByIdProps) =>
  useQuery({
    queryKey: [QueryKeys.getSettingsByAgencyId, id, { params }],
    queryFn: async (): Promise<{ [key: string]: string } | undefined> => {
      try {
        const result: AxiosResponse<{ [key: string]: string }, any> = await client.get(`settings/agency/${id}`, {
          params,
        });

        return result.data;
      } catch (e) {
        onErrorCallback && onErrorCallback(e as AxiosError);
      }
    },
    refetchOnWindowFocus: true,
    enabled: Boolean(id),
  });

export const useGetPublicSettingsByAgencyId = ({ id, onErrorCallback }: GetByByIdProps) =>
  useQuery({
    queryKey: [QueryKeys.getSettingsByAgencyId, id],
    queryFn: async (): Promise<{ [key: string]: string } | undefined> => {
      try {
        const result: AxiosResponse<{ [key: string]: string }, any> = await client.get(`settings/public/agency/${id}`);

        return result.data;
      } catch (e) {
        onErrorCallback && onErrorCallback(e as AxiosError);
      }
    },
    refetchOnWindowFocus: true,
    enabled: Boolean(id),
  });

export const useGetSupportFormsByAgency = ({
  agencyId,
  currentPage,
  pageSize,
  search,
  onErrorCallback,
}: GetSupportFormsByAgency) =>
  useQuery({
    queryKey: [QueryKeys.getSupportFormsByAgency, agencyId, currentPage, pageSize, search],
    queryFn: async (): Promise<PagedResponse<CaseSupportFormResponseDto> | null> => {
      try {
        const result = await client.get<PagedResponse<CaseSupportFormResponseDto>>(
          `case-support-form/agency/${agencyId}`,
          {
            params: {
              currentPage,
              pageSize,
              search,
            },
          },
        );
        return result.data;
      } catch (e) {
        onErrorCallback && onErrorCallback(e as AxiosError);
        return null;
      }
    },
    refetchOnWindowFocus: true,
    enabled: Boolean(agencyId),
  });

export const useGetSupportFormById = ({ id, onErrorCallback }: GetByByIdProps) =>
  useQuery({
    queryKey: [QueryKeys.getSupportFormById, id],
    queryFn: async (): Promise<CaseSupportFormResponseDto | null> => {
      try {
        const result = await client.get<CaseSupportFormResponseDto>(`case-support-form/${id}`);

        return result.data;
      } catch (e) {
        onErrorCallback && onErrorCallback(e as AxiosError);
        return null;
      }
    },
    refetchOnWindowFocus: true,
    enabled: Boolean(id) && id !== NEW_SUPPORT_FORM_ID,
  });

// MUTATIONS
const createCaseSupporForm = async (data: CaseSupportFormRequestDto & { id?: string }) => {
  const { id, logo, ...payload } = data;
  const formData = new FormData();
  if (logo) {
    formData.append('logo', logo);
  }
  formData.append('body', JSON.stringify(payload));
  if (id === NEW_SUPPORT_FORM_ID) {
    return client.post<CaseSupportFormResponseDto>('case-support-form', formData);
  } else {
    return client.put<CaseSupportFormResponseDto>(`case-support-form/${id}`, formData);
  }
};

export const useCreateCaseSupporForm = (
  options?:
    | Omit<
        UseMutationOptions<
          AxiosResponse<CaseSupportFormResponseDto, any>,
          unknown,
          CaseSupportFormRequestDto & { id?: string },
          unknown
        >,
        'mutationFn'
      >
    | undefined,
) => useMutation((data) => createCaseSupporForm(data), options);

interface UpdateAgencySettingsParams {
  agencyId: string | number;
  settings: Record<string, string>;
}

const updateAgencySettings = async ({ agencyId, settings }: UpdateAgencySettingsParams) =>
  client.post<{ [key: string]: string }>(`settings/agency/${agencyId}`, settings);

export const useUpdateAgencySettings = (
  options?: Omit<
    UseMutationOptions<AxiosResponse<{ [key: string]: string }, any>, unknown, UpdateAgencySettingsParams, unknown>,
    'mutationFn'
  >,
) => useMutation((params) => updateAgencySettings(params), options);
